// frontend/src/pages/Gallery.js
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService, getFullImageUrl } from '../services/api';
import { 
  Search, 
  Filter, 
  Grid, 
  List, 
  X, 
  Eye, 
  Download, 
  Play,
  Image,
  Video,
  SortAsc,
  SortDesc
} from 'lucide-react';

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('grid');
  const [contentType, setContentType] = useState('all'); // all, images, videos
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaType, setMediaType] = useState(null); // image or video

  const category = searchParams.get('category');
  const search = searchParams.get('search');
  const sort = searchParams.get('sort') || '-created_at';

  useEffect(() => {
    fetchGalleryData();
  }, [category, search, sort, contentType]);

  const fetchGalleryData = async () => {
    try {
      setLoading(true);
      
      // دریافت دسته‌بندی‌ها
      const categoriesResponse = await apiService.getGalleryCategories();
      setCategories(categoriesResponse.data.results || categoriesResponse.data);
      
      // پارامترهای مشترک
      const commonParams = {};
      if (category) commonParams.category = category;
      if (search) commonParams.search = search;
      if (sort) commonParams.sort = sort;

      // دریافت تصاویر
      if (contentType === 'all' || contentType === 'images') {
        const imagesResponse = await apiService.getGalleryImages(commonParams);
        setImages(imagesResponse.data.results || imagesResponse.data);
      } else {
        setImages([]);
      }

      // دریافت ویدیوها
      if (contentType === 'all' || contentType === 'videos') {
        const videosResponse = await apiService.getGalleryVideos(commonParams);
        setVideos(videosResponse.data.results || videosResponse.data);
      } else {
        setVideos([]);
      }

    } catch (error) {
      console.error('Error fetching gallery data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const searchValue = formData.get('search');
    
    const params = new URLSearchParams(searchParams);
    if (searchValue) {
      params.set('search', searchValue);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const handleCategoryChange = (categorySlug) => {
    const params = new URLSearchParams(searchParams);
    
    if (categorySlug) {
      params.set('category', categorySlug);
    } else {
      params.delete('category');
    }
    
    setSearchParams(params);
  };

  const handleSortChange = (newSort) => {
    const params = new URLSearchParams(searchParams);
    params.set('sort', newSort);
    setSearchParams(params);
  };

  const handleContentTypeChange = (type) => {
    setContentType(type);
  };

  const clearFilters = () => {
    setSearchParams({});
    setContentType('all');
  };

  const openMediaModal = (media, type) => {
    setSelectedMedia(media);
    setMediaType(type);
  };

  const closeMediaModal = () => {
    setSelectedMedia(null);
    setMediaType(null);
  };

  const handleDownload = async (image) => {
    try {
      // افزایش تعداد دانلود
      // await apiService.downloadImage(image.id);
      
      // ایجاد لینک دانلود
      const link = document.createElement('a');
      link.href = getFullImageUrl(image.image);
      link.download = `${image.title}.${image.image.split('.').pop()}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      alert('تصویر در حال دانلود است...');
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  const getSortOptions = () => [
    { value: '-created_at', label: 'جدیدترین', icon: SortDesc },
    { value: 'created_at', label: 'قدیمی‌ترین', icon: SortAsc },
    { value: 'title', label: 'الفبایی (صعودی)', icon: SortAsc },
    { value: '-title', label: 'الفبایی (نزولی)', icon: SortDesc },
    { value: '-view_count', label: 'پربازدیدترین', icon: SortDesc },
  ];

  const getActiveFiltersCount = () => {
    let count = 0;
    if (category) count++;
    if (search) count++;
    if (contentType !== 'all') count++;
    return count;
  };

  const getAllMedia = () => {
    if (contentType === 'images') return images;
    if (contentType === 'videos') return videos;
    return [...images, ...videos];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">📸 گالری تکنو پلیمر اسپادانا</h1>
          <p className="text-gray-400 text-lg">گالری تصاویر و ویدیوهای پروژه‌ها و محصولات ما</p>
        </div>

        {/* Filters and Search */}
        <div className="bg-gray-900 rounded-2xl p-6 mb-8 border border-gray-800">
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 w-full lg:w-auto">
              <div className="relative">
                <input
                  type="text"
                  name="search"
                  placeholder="جستجو در گالری..."
                  defaultValue={search}
                  className="w-full lg:w-80 pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                />
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              </div>
            </form>

            <div className="flex items-center space-x-4 space-x-reverse">
              {/* Sort Options */}
              <select
                value={sort}
                onChange={(e) => handleSortChange(e.target.value)}
                className="bg-gray-800 border border-gray-700 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
              >
                {getSortOptions().map(option => {
                  const Icon = option.icon;
                  return (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  );
                })}
              </select>

              {/* View Mode Toggle */}
              <div className="flex items-center space-x-2 space-x-reverse bg-gray-800 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'grid' ? 'bg-gray-700 text-yellow-400' : 'text-gray-400'
                  }`}
                >
                  <Grid size={20} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'list' ? 'bg-gray-700 text-yellow-400' : 'text-gray-400'
                  }`}
                >
                  <List size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Categories and Content Type Filters */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* Content Type Filter */}
            <div>
              <div className="flex items-center space-x-2 space-x-reverse mb-4">
                <span className="font-medium text-gray-300">نوع محتوا:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'all', label: 'همه', icon: null },
                  { value: 'images', label: 'تصاویر', icon: Image },
                  { value: 'videos', label: 'ویدیوها', icon: Video },
                ].map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      onClick={() => handleContentTypeChange(type.value)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center space-x-2 space-x-reverse ${
                        contentType === type.value
                          ? 'bg-yellow-400 text-gray-900 shadow-lg'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      {Icon && <Icon size={16} />}
                      <span>{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Categories Filter */}
            <div>
              <div className="flex items-center space-x-2 space-x-reverse mb-4">
                <Filter size={20} className="text-gray-400" />
                <span className="font-medium text-gray-300">دسته‌بندی‌ها:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleCategoryChange(null)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    !category 
                      ? 'bg-yellow-400 text-gray-900 shadow-lg' 
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  همه دسته‌بندی‌ها
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      category === cat.slug
                        ? 'bg-yellow-400 text-gray-900 shadow-lg'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {cat.name} ({cat.images_count})
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Active Filters */}
          {getActiveFiltersCount() > 0 && (
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-800">
              <div className="flex items-center space-x-2 space-x-reverse">
                <span className="text-gray-300 text-sm">
                  {getAllMedia().length} مورد یافت شد
                </span>
                {(search || category || contentType !== 'all') && (
                  <span className="text-gray-400 text-sm">
                    ({getActiveFiltersCount()} فیلتر فعال)
                  </span>
                )}
              </div>
              <button
                onClick={clearFilters}
                className="flex items-center space-x-2 space-x-reverse text-gray-400 hover:text-white transition-colors text-sm"
              >
                <X size={16} />
                <span>پاک کردن فیلترها</span>
              </button>
            </div>
          )}
        </div>

        {/* Media Grid/List */}
        {getAllMedia().length === 0 ? (
          <div className="text-center py-16">
            <Image className="text-gray-400 h-16 w-16 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-white mb-2">موردی یافت نشد</h3>
            <p className="text-gray-400 mb-6">لطفاً فیلترهای جستجو را تغییر دهید.</p>
            <button
              onClick={clearFilters}
              className="bg-yellow-400 text-gray-900 px-6 py-2 rounded-lg hover:bg-yellow-300 transition-colors"
            >
              نمایش همه موارد
            </button>
          </div>
        ) : (
          <div className={
            viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
              : 'space-y-6'
          }>
            {/* نمایش تصاویر */}
            {images.map((image) => (
              <MediaCard 
                key={`image-${image.id}`} 
                media={image} 
                type="image"
                viewMode={viewMode}
                onOpen={() => openMediaModal(image, 'image')}
                onDownload={() => handleDownload(image)}
              />
            ))}

            {/* نمایش ویدیوها */}
            {videos.map((video) => (
              <MediaCard 
                key={`video-${video.id}`} 
                media={video} 
                type="video"
                viewMode={viewMode}
                onOpen={() => openMediaModal(video, 'video')}
              />
            ))}
          </div>
        )}
      </div>

      {/* Media Modal */}
      {selectedMedia && (
        <MediaModal 
          media={selectedMedia}
          type={mediaType}
          onClose={closeMediaModal}
          onDownload={mediaType === 'image' ? () => handleDownload(selectedMedia) : null}
        />
      )}
    </div>
  );
};

// کامپوننت کارت رسانه
const MediaCard = ({ media, type, viewMode, onOpen, onDownload }) => {
  if (viewMode === 'list') {
    return (
      <div className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 hover:border-yellow-400 transition-all group">
        <div className="flex flex-col md:flex-row">
          <div className="md:w-64 flex-shrink-0 relative">
            {type === 'image' ? (
              <img
                src={getFullImageUrl(media.image)}
                alt={media.alt_text || media.title}
                className="w-full h-48 md:h-full object-cover group-hover:scale-105 transition-transform duration-500 cursor-pointer"
                onClick={onOpen}
              />
            ) : (
              <div 
                className="w-full h-48 md:h-full bg-gray-800 relative cursor-pointer group/video"
                onClick={onOpen}
              >
                <img
                  src={getFullImageUrl(media.thumbnail) || getFullImageUrl('/static/images/video-placeholder.jpg')}
                  alt={media.title}
                  className="w-full h-full object-cover group-hover/video:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                  <div className="bg-yellow-400 text-gray-900 p-4 rounded-full hover:bg-yellow-300 transition-colors">
                    <Play size={24} fill="currentColor" />
                  </div>
                </div>
              </div>
            )}
            
            {/* Badge */}
            <div className="absolute top-3 left-3">
              <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                type === 'image' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-red-500 text-white'
              }`}>
                {type === 'image' ? 'تصویر' : 'ویدیو'}
              </span>
            </div>
          </div>

          <div className="flex-1 p-6">
            <h3 className="text-xl font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors">
              {media.title}
            </h3>
            
            {media.category_name && (
              <p className="text-gray-400 text-sm mb-3">
                دسته‌بندی: {media.category_name}
              </p>
            )}
            
            <p className="text-gray-300 mb-4 line-clamp-3">
              {media.description}
            </p>
            
            <div className="flex items-center justify-between text-gray-400 text-sm">
              <div className="flex items-center space-x-4 space-x-reverse">
                {type === 'image' && (
                  <>
                    <div className="flex items-center space-x-1 space-x-reverse">
                      <Eye size={14} />
                      <span>{media.view_count || 0} بازدید</span>
                    </div>
                    <div className="flex items-center space-x-1 space-x-reverse">
                      <Download size={14} />
                      <span>{media.download_count || 0} دانلود</span>
                    </div>
                  </>
                )}
                <span>
                  {new Date(media.created_at).toLocaleDateString('fa-IR')}
                </span>
              </div>
              
              <div className="flex items-center space-x-3 space-x-reverse">
                {type === 'image' && onDownload && (
                  <button
                    onClick={onDownload}
                    className="p-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
                    title="دانلود تصویر"
                  >
                    <Download size={18} />
                  </button>
                )}
                <button
                  onClick={onOpen}
                  className="bg-yellow-400 text-gray-900 px-4 py-2 rounded-lg hover:bg-yellow-300 transition-colors font-medium"
                >
                  {type === 'image' ? 'مشاهده' : 'پخش ویدیو'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Grid View
  return (
    <div className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 hover:border-yellow-400 transition-all group">
      <div className="relative h-48 overflow-hidden">
        {type === 'image' ? (
          <img
            src={getFullImageUrl(media.image)}
            alt={media.alt_text || media.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 cursor-pointer"
            onClick={onOpen}
          />
        ) : (
          <div 
            className="w-full h-full bg-gray-800 relative cursor-pointer group/video"
            onClick={onOpen}
          >
            <img
              src={getFullImageUrl(media.thumbnail) || getFullImageUrl('/static/images/video-placeholder.jpg')}
              alt={media.title}
              className="w-full h-full object-cover group-hover/video:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
              <div className="bg-yellow-400 text-gray-900 p-3 rounded-full hover:bg-yellow-300 transition-colors">
                <Play size={20} fill="currentColor" />
              </div>
            </div>
          </div>
        )}
        
        {/* Badge */}
        <div className="absolute top-3 left-3">
          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
            type === 'image' 
              ? 'bg-blue-500 text-white' 
              : 'bg-red-500 text-white'
          }`}>
            {type === 'image' ? 'تصویر' : 'ویدیو'}
          </span>
        </div>

        {/* Actions */}
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex space-x-2 space-x-reverse">
            {type === 'image' && onDownload && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload();
                }}
                className="p-2 bg-black bg-opacity-50 text-white rounded-lg hover:bg-opacity-70 transition-colors"
                title="دانلود تصویر"
              >
                <Download size={16} />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
              className="p-2 bg-black bg-opacity-50 text-white rounded-lg hover:bg-opacity-70 transition-colors"
              title={type === 'image' ? 'مشاهده تصویر' : 'پخش ویدیو'}
            >
              {type === 'image' ? <Eye size={16} /> : <Play size={16} fill="currentColor" />}
            </button>
          </div>
        </div>
      </div>
      
      <div className="p-4">
        <h3 className="font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors line-clamp-2">
          {media.title}
        </h3>
        
        {media.category_name && (
          <p className="text-gray-400 text-xs mb-2">
            {media.category_name}
          </p>
        )}

        <p className="text-gray-300 text-sm mb-3 line-clamp-2">
          {media.description}
        </p>

        <div className="flex items-center justify-between text-gray-400 text-xs">
          {type === 'image' ? (
            <>
              <div className="flex items-center space-x-1 space-x-reverse">
                <Eye size={12} />
                <span>{media.view_count || 0}</span>
              </div>
              <span>
                {new Date(media.created_at).toLocaleDateString('fa-IR')}
              </span>
            </>
          ) : (
            <span>
              {new Date(media.created_at).toLocaleDateString('fa-IR')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// کامپوننت مودال نمایش رسانه
const MediaModal = ({ media, type, onClose, onDownload }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    // جلوگیری از اسکرول body وقتی مودال باز است
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-gray-900 rounded-2xl max-w-4xl max-h-full overflow-auto relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 left-4 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-colors"
        >
          <X size={24} />
        </button>

        {/* Download Button for Images */}
        {type === 'image' && onDownload && (
          <button
            onClick={onDownload}
            className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-colors"
            title="دانلود تصویر"
          >
            <Download size={24} />
          </button>
        )}

        {/* Media Content */}
        <div className="p-6">
          {type === 'image' ? (
            <div className="relative">
              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
                </div>
              )}
              <img
                src={getFullImageUrl(media.image)}
                alt={media.alt_text || media.title}
                className={`w-full h-auto max-h-[70vh] object-contain rounded-lg ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                } transition-opacity duration-300`}
                onLoad={() => setImageLoaded(true)}
              />
            </div>
          ) : (
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
              {media.video_file ? (
                <video
                  controls
                  className="w-full h-full"
                  poster={getFullImageUrl(media.thumbnail)}
                >
                  <source src={getFullImageUrl(media.video_file)} type="video/mp4" />
                  مرورگر شما از پخش ویدیو پشتیبانی نمی‌کند.
                </video>
              ) : media.video_url ? (
                <iframe
                  src={media.video_url}
                  className="w-full h-full"
                  allowFullScreen
                  title={media.title}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  لینک ویدیو در دسترس نیست
                </div>
              )}
            </div>
          )}

          {/* Media Info */}
          <div className="mt-6">
            <h2 className="text-2xl font-bold text-white mb-2">{media.title}</h2>
            
            {media.category_name && (
              <p className="text-yellow-400 mb-3">
                دسته‌بندی: {media.category_name}
              </p>
            )}

            <p className="text-gray-300 leading-relaxed mb-4">
              {media.description}
            </p>

            <div className="flex items-center justify-between text-gray-400 text-sm">
              <div className="flex items-center space-x-4 space-x-reverse">
                {type === 'image' && (
                  <>
                    <div className="flex items-center space-x-1 space-x-reverse">
                      <Eye size={14} />
                      <span>{media.view_count || 0} بازدید</span>
                    </div>
                    <div className="flex items-center space-x-1 space-x-reverse">
                      <Download size={14} />
                      <span>{media.download_count || 0} دانلود</span>
                    </div>
                  </>
                )}
                <span>
                  تاریخ آپلود: {new Date(media.created_at).toLocaleDateString('fa-IR')}
                </span>
              </div>

              {type === 'image' && media.dimensions && (
                <span className="text-gray-400">
                  ابعاد: {media.dimensions}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Gallery;